import React from 'react';
import produce from 'immer';
import { Slider } from 'antd';
import cn from 'classnames';

import SwitchButton from 'admin/components/common/Form/SwitchButton';
import { Label } from 'admin/components/pages/Story/CardEditor/Inspector/PropField/Label';
import { EventEmitter } from 'src/common/utils/event-emitter';
import OffsetControl, { OFFSET_MAX } from './OffsetControl';
import type { BoxShadowData, TextShadowData } from './types';
import { isBoxShadow } from './helpers';
import css from './BoxShadowSettings.scss';

type ShadowData = BoxShadowData | TextShadowData;
type Props<T extends ShadowData> = {
	className?: string;
	initialData: T;
	onChange: (state: T) => void;
	onChangeComplete: (state: T) => void;
	spreadMax?: number;
	blurMax?: number;
};

type State<T extends ShadowData> = T & {
	isPristine: boolean;
};

type Action<T extends ShadowData> =
	| { type: 'SET_SHADOW_TYPE'; payload: string }
	| { type: 'SET_X_OFFSET'; payload: number }
	| { type: 'SET_Y_OFFSET'; payload: number }
	| { type: 'SET_BLUR'; payload: number }
	| { type: 'SET_SPREAD'; payload: number }
	| { type: 'RESET_TO'; payload: T };

function reducer<T extends ShadowData>(state: State<T>, action: Action<T>) {
	return produce(state, draft => {
		if (action.type !== 'RESET_TO') {
			draft.isPristine = false;
		}
		switch (action.type) {
			case 'SET_SHADOW_TYPE':
				if (isBoxShadow(draft)) {
					draft.shadowType = action.payload;
				}
				break;
			case 'SET_X_OFFSET':
				draft.xOffset = action.payload;
				break;
			case 'SET_Y_OFFSET':
				draft.yOffset = action.payload;
				break;
			case 'SET_BLUR':
				draft.blur = action.payload;
				break;
			case 'SET_SPREAD':
				if (isBoxShadow(draft)) {
					draft.spread = action.payload;
				}
				break;
			case 'RESET_TO':
				return { ...action.payload, isPristine: true };
			default:
				return state;
		}

		return draft;
	}) as State<T>;
}

const TYPE_OPTIONS = [
	{ label: 'Drop', value: 'outset' },
	{ label: 'Inner', value: 'inset' },
];

const BLUR_MAX = 100;
const SPREAD_MAX = 100;

export const Controls = <T extends ShadowData>(props: Props<T>) => {
	const { className = '', onChange, onChangeComplete, blurMax = BLUR_MAX, spreadMax = SPREAD_MAX } = props;
	const [state, dispatch] = React.useReducer<React.Reducer<State<T>, Action<T>>>(reducer, {
		...props.initialData,
		isPristine: true,
	});
	const eventEmitterRef = React.useRef(new EventEmitter<{ type: 'complete' }>());

	const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select();
	};

	const handleOffsetUpdate = React.useCallback((x: number, y: number) => {
		// x and y are percentage rage from -1 to 1
		dispatch({ type: 'SET_X_OFFSET', payload: Math.round(x * OFFSET_MAX) });
		dispatch({ type: 'SET_Y_OFFSET', payload: Math.round(y * OFFSET_MAX) });
	}, []);

	// A new initialData (another layer or element) restarts the controls from it
	React.useLayoutEffect(() => {
		dispatch({ type: 'RESET_TO', payload: props.initialData });
	}, [props.initialData]);

	React.useEffect(() => {
		if (!state.isPristine) {
			onChange(state);
		}
	}, [onChange, state]);

	React.useEffect(() => {
		const eventEmitter = eventEmitterRef.current;

		eventEmitter.addListener('complete', () => {
			onChangeComplete(state);
		});

		return () => {
			eventEmitter.destroy();
		};
	}, [state, onChangeComplete]);

	return (
		<div className={cn(css.controls, className)}>
			{isBoxShadow(state) && (
				<label className={css.control}>
					<Label>Type</Label>
					<div className={css.input}>
						<SwitchButton
							className={css.switch}
							position={state.shadowType === 'outset' ? 'left' : 'right'}
							leftButtonText={TYPE_OPTIONS[0].label}
							rightButtonText={TYPE_OPTIONS[1].label}
							onSwitch={position => {
								const nextValue = TYPE_OPTIONS[position === 'left' ? 0 : 1].value;

								dispatch({
									type: 'SET_SHADOW_TYPE',
									payload: nextValue,
								});

								onChangeComplete({ ...state, shadowType: nextValue });
							}}
						/>
					</div>
				</label>
			)}
			<div className={css.offsets}>
				<div className={css.inputs}>
					<label className={css.control}>
						<Label>X</Label>
						<div className={css.input}>
							<input
								type="number"
								value={state.xOffset}
								onFocus={handleInputFocus}
								onChange={e => dispatch({ type: 'SET_X_OFFSET', payload: +e.target.value })}
								onBlur={() => onChangeComplete(state)}
							/>
						</div>
					</label>
					<label className={css.control}>
						<Label>Y</Label>
						<div className={css.input}>
							<input
								type="number"
								value={state.yOffset}
								onFocus={handleInputFocus}
								onChange={e => dispatch({ type: 'SET_Y_OFFSET', payload: +e.target.value })}
								onBlur={() => onChangeComplete(state)}
							/>
						</div>
					</label>
				</div>
				<div className={css.dndBlock}>
					<div className={css.dndBlockContent}>
						<OffsetControl
							offsetX={state.xOffset / OFFSET_MAX}
							offsetY={state.yOffset / OFFSET_MAX}
							eventEmitter={eventEmitterRef.current}
							onUpdate={handleOffsetUpdate}
						/>
					</div>
					<div className={css.dndInnerPusher} />
				</div>
			</div>
			<div className={css.blur}>
				<div className={css.inputs}>
					<label className={css.control}>
						<Label>Blur</Label>
						<div className={css.input}>
							<input
								type="number"
								min={0}
								max={blurMax}
								value={state.blur}
								onFocus={handleInputFocus}
								onChange={e => dispatch({ type: 'SET_BLUR', payload: +e.target.value })}
								onBlur={() => onChangeComplete(state)}
							/>
						</div>
					</label>
				</div>
				<div className={css.dndBlock}>
					<div className={css.dndBlockContent}>
						<Slider
							className={css.slider}
							value={state.blur}
							max={blurMax}
							onChange={v => dispatch({ type: 'SET_BLUR', payload: v })}
							onChangeComplete={v => onChangeComplete({ ...state, blur: v })}
						/>
					</div>
					<div className={css.dndInnerPusher} />
				</div>
			</div>
			{isBoxShadow(state) && (
				<div className={css.spread}>
					<div className={css.inputs}>
						<label className={css.control}>
							<Label>Spread</Label>
							<div className={css.input}>
								<input
									type="number"
									min={-spreadMax}
									max={spreadMax}
									value={state.spread}
									onFocus={handleInputFocus}
									onChange={e => dispatch({ type: 'SET_SPREAD', payload: +e.target.value })}
									onBlur={() => onChangeComplete(state)}
								/>
							</div>
						</label>
					</div>
					<div className={css.dndBlock}>
						<div className={css.dndBlockContent}>
							<Slider
								className={css.slider}
								value={state.spread}
								min={-spreadMax}
								max={spreadMax}
								onChange={v => dispatch({ type: 'SET_SPREAD', payload: v })}
								onChangeComplete={v => onChangeComplete({ ...state, spread: v })}
							/>
						</div>
						<div className={css.dndInnerPusher} />
					</div>
				</div>
			)}
		</div>
	);
};

export default Controls;
